import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Consulenza from "@/components/landing/Consulenza";
import Contact from "@/components/landing/Contact";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import CookieConsent from "@/components/landing/CookieConsent";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";

const CALENDAR_URL =
  "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1Uy8H-tbIBGE0qbOrbAWnHA4KzFPCjRz4IejemGD-zNYnoUALlZoFgshQPOHkR9vqxr7H_iGR_";

export default function ConsulenzaPage() {
  return (
    <main
      data-testid="consulenza-page-root"
      className="min-h-screen bg-[#F9F8F6] text-[#0B192C]"
    >
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-[#E5E1DA]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="font-serif-display text-xl tracking-tight"
            data-testid="consulenza-brand"
          >
            Metodo<span className="text-[#E89B6C]">·</span>di
            <span className="text-[#E89B6C]">·</span>Mare
          </Link>
          <div className="flex items-center gap-3 md:gap-6">
            <Link
              to="/"
              data-testid="consulenza-back-home"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#0E7C8F]"
            >
              <ArrowLeft size={14} />
              <span className="hidden sm:inline">Vai al libro</span>
              <span className="sm:hidden">Libro</span>
            </Link>
            <Link
              to="/certificazione"
              data-testid="consulenza-to-certificazione"
              className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#0E7C8F]"
            >
              Certificazione
            </Link>
            <a
              href={CALENDAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="consulenza-page-cta-nav"
              className="px-5 py-3 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#1E3557] transition-colors"
            >
              Prenota call
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-20 md:pb-28 overflow-hidden bg-gradient-to-br from-[#0B192C] via-[#173F5F] to-[#0E7C8F] text-[#F9F8F6]">
        <div
          aria-hidden
          className="absolute -top-32 right-0 w-[50%] h-[80%] opacity-30 blur-3xl pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, #E89B6C 0%, transparent 70%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#E89B6C]/40 bg-[#E89B6C]/10 mb-6">
              <span className="overline text-[#E89B6C]">
                Consulenza professionale
              </span>
            </div>
            <h1 className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight">
              Trasforma la tua
              <br />
              <em className="text-[#E89B6C]">attività digitale</em>
              <br />
              in 90 giorni.
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl font-light leading-relaxed text-[#F9F8F6]/80">
              Affianco aziende e professionisti col{" "}
              <em>Metodo di Mare</em>: strategia digitale, leadership operativa,
              cybersicurezza, sviluppo di reti commerciali e funnel di vendita.
              9 servizi su misura, una sola call gratuita per capire da dove
              partire.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href={CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="consulenza-page-cta-primary"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#E89B6C] text-[#0B192C] text-sm uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-all"
              >
                <Calendar size={16} />
                Prenota la call gratuita
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href="https://wa.me/393773660476?text=Ciao%20Michele%2C%20vorrei%20informazioni%20sulla%20consulenza."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="consulenza-page-cta-whatsapp"
                className="inline-flex items-center gap-3 px-7 py-4 border border-[#F9F8F6]/30 text-[#F9F8F6] text-sm uppercase tracking-[0.2em] hover:bg-[#F9F8F6]/10 transition-colors"
              >
                <WhatsAppIcon size={16} className="text-[#25D366]" />
                WhatsApp diretto
              </a>
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl">
              <Stat n="9" l="Servizi" />
              <Stat n="12+" l="Anni esperienza" />
              <Stat n="2.400+" l="Professionisti formati" />
              <Stat n="L.4/13" l="Certificato" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Reused sections */}
      <Consulenza />
      <Contact />

      <Footer />
      <WhatsAppFloat />
      <CookieConsent />
    </main>
  );
}

function Stat({ n, l }) {
  return (
    <div className="border-t border-[#F9F8F6]/20 pt-4">
      <p className="font-serif-display text-3xl md:text-4xl text-[#E89B6C]">{n}</p>
      <p className="overline mt-1 text-[#F9F8F6]/70">{l}</p>
    </div>
  );
}
